import { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { StatusState } from '../../../../../../FrontEndReact/src/Core/Hooks/useBizQuery';
import { User } from '../../../../../../FrontEndReact/src/Core/Types/user';
import { Unit } from '../../../../../../FrontEndReact/src/Core/Types/unit';
import BizUnitSelect from '../../../../../../FrontEndReact/src/Core/Components/Mini/BizUnitSelect';
import { BizButton } from '../../../../../../FrontEndReact/src/Core/Components/Mini/BizButton';
import { BizSelect } from '../../../../../../Js/React/UI/BizSelect/BizSelect';
import {
	useAllowedStudentRegistrationUnitTypeIds,
	getUserDisplayName,
	useHomeworkHelpers,
	useSaveHomeworkHelperStudentLink,
	useStudents,
} from '../../Hooks/useStudentRegistration';

const HOMEWORK_HELPER_RELATION_SYSTEM_NAME = 'HOMEWORKHELPER_STUDENT';

type LaxhjalpenHomeworkHelperStudentLinkPanelProps = {
	t: TFunction;
	variant: 'admin' | 'public';
};

const LaxhjalpenHomeworkHelperStudentLinkPanel = ({ t, variant }: LaxhjalpenHomeworkHelperStudentLinkPanelProps) => {
	const isAdmin = variant === 'admin';
	const [unitId, setUnitId] = useState(-1);
	const [unitTypeId, setUnitTypeId] = useState(-1);
	const [helperSelections, setHelperSelections] = useState<Record<number, number>>({});
	const [savingStudentId, setSavingStudentId] = useState<number | null>(null);
	const { allowedUnitTypeIds } = useAllowedStudentRegistrationUnitTypeIds();
	const isUnitTypeAllowed = unitId <= 0 || allowedUnitTypeIds.includes(unitTypeId);
	const hasAllowedUnit = unitId > 0 && isUnitTypeAllowed;
	const { helperOptions } = useHomeworkHelperOptions(hasAllowedUnit ? unitId : -1);
	const { students, isLoading } = useStudents(unitId, hasAllowedUnit);
	const { saveStatus, saveResult, saveHomeworkHelperStudentLink } = useSaveHomeworkHelperStudentLink(unitId);
	const panelClassName = isAdmin
		? 'admin-panel margin-top-20 biz-laxhjalpen-helper-link-panel'
		: 'biz-laxhjalpen-student-registration-public__panel biz-laxhjalpen-helper-link-panel';
	const fieldClassName = isAdmin ? 'margin-top' : 'biz-laxhjalpen-student-registration-public__field';
	const labelClassName = isAdmin ? 'control-label' : 'biz-laxhjalpen-student-registration-public__label';
	const studentLabel = t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.STUDENT_SECTION');
	const emailLabel = t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.EMAIL');
	const homeworkHelperLabel = t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.HOMEWORK_HELPER');

	useEffect(() => {
		const selections = students.reduce<Record<number, number>>((current, student) => {
			current[student.Id] = getCurrentHomeworkHelperId(student);
			return current;
		}, {});
		setHelperSelections((current) => (areHelperSelectionsEqual(current, selections) ? current : selections));
	}, [students]);

	useEffect(() => {
		if (saveStatus !== 'LOADING') {
			setSavingStudentId(null);
		}
	}, [saveStatus]);

	function onUnitChange(unit: Unit | null) {
		setUnitId(unit?.Id ?? -1);
		setUnitTypeId(unit?.UnitTypeId ?? -1);
		setHelperSelections({});
	}

	function onHelperChange(studentId: number, helperUserId: number) {
		setHelperSelections((current) => ({
			...current,
			[studentId]: helperUserId,
		}));
	}

	function onSave(studentId: number) {
		const homeworkHelperUserId = helperSelections[studentId] ?? -1;
		if (!hasAllowedUnit || homeworkHelperUserId <= 0) {
			return;
		}

		setSavingStudentId(studentId);
		saveHomeworkHelperStudentLink({
			unitId,
			studentUserId: studentId,
			homeworkHelperUserId,
		});
	}

	return (
		<div className={panelClassName}>
			<div className='biz-laxhjalpen-helper-link-panel__header'>
				<div>
					<span className={`${labelClassName} biz-laxhjalpen-student-registration__section-title`}>
						{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LINK_HELPER_SECTION')}
					</span>
					<p className='biz-laxhjalpen-helper-link-panel__intro'>
						{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LINK_HELPER_INTRO')}
					</p>
				</div>
			</div>

			<div className={fieldClassName}>
				<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.UNIT')}</span>
				<BizUnitSelect
					accessTypeKey='USERS'
					selectedId={unitId}
					onChange={onUnitChange}
					noSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.NO_UNIT')}
					childNoSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.CHILD_NO_UNIT')}
					direction='horizontal'
				/>
				{unitId > 0 && !isUnitTypeAllowed && (
					<div className='biz-laxhjalpen-student-registration__validation-message'>
						{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.UNIT_MUST_BE_GROUP_FOR_LINK')}
					</div>
				)}
			</div>

			{saveResult?.Success === false && (
				<div className='biz-laxhjalpen-helper-link-panel__state biz-laxhjalpen-helper-link-panel__state--error'>
					{saveResult.Message || t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LINK_HELPER_GENERAL_ERROR')}
				</div>
			)}

			{hasAllowedUnit && isLoading && (
				<div className='biz-laxhjalpen-helper-link-panel__empty'>
					{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LINK_HELPER_LOADING')}
				</div>
			)}

			{hasAllowedUnit && !isLoading && students.length === 0 && (
				<div className='biz-laxhjalpen-helper-link-panel__empty'>
					{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LINK_HELPER_NO_STUDENTS')}
				</div>
			)}

			{hasAllowedUnit && !isLoading && students.length > 0 && (
				<div className='biz-laxhjalpen-helper-link-panel__table-wrapper'>
					<table className='biz-laxhjalpen-helper-link-panel__table'>
						<thead>
							<tr>
								<th>{studentLabel}</th>
								<th>{emailLabel}</th>
								<th>{homeworkHelperLabel}</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{students.map((student) => {
								const selectedHelperId = helperSelections[student.Id] ?? -1;
								const rowSaveStatus: StatusState =
									savingStudentId === student.Id && saveStatus === 'LOADING' ? 'LOADING' : 'INITIAL';

								return (
									<tr key={student.Id}>
										<td data-label={studentLabel}>{getUserDisplayName(student)}</td>
										<td data-label={emailLabel}>{student.Email}</td>
										<td data-label={homeworkHelperLabel}>
											<BizSelect
												name={`HomeworkHelper_${student.Id}`}
												className='form-control'
												options={helperOptions}
												selectedId={selectedHelperId}
												onChange={(helper) => onHelperChange(student.Id, helper?.Id ?? -1)}
												noSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.NO_HOMEWORK_HELPER_LINK')}
												disabled={helperOptions.length === 0}
											/>
										</td>
										<td className='biz-laxhjalpen-helper-link-panel__actions'>
											<BizButton
												text={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.SAVE_LINK')}
												onClick={() => onSave(student.Id)}
												legacy={isAdmin}
												disabled={selectedHelperId <= 0 || saveStatus === 'LOADING'}
												state={rowSaveStatus}
											/>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default LaxhjalpenHomeworkHelperStudentLinkPanel;

function useHomeworkHelperOptions(unitId: number) {
	const { helpers } = useHomeworkHelpers(unitId);
	const helperOptions = helpers.map((helper) => ({
		Id: helper.Id,
		Name: `${helper.Name}${helper.Email ? ` (${helper.Email})` : ''}`,
	}));

	return {
		helperOptions,
	};
}

function getCurrentHomeworkHelper(student: User) {
	const relation = student.Relations?.find(
		(relation) =>
			relation.RelationTypeSystemName === HOMEWORK_HELPER_RELATION_SYSTEM_NAME &&
			(relation.ChildUserId === student.Id || relation.ParentUserId === student.Id)
	);

	if (!relation) {
		return null;
	}

	if (relation.ChildUserId === student.Id) {
		return {
			id: relation.ParentUserId,
			displayName: relation.ParentUserDisplayName,
		};
	}

	return {
		id: relation.ChildUserId,
		displayName: relation.ChildUserDisplayName,
	};
}

function getCurrentHomeworkHelperId(student: User) {
	return getCurrentHomeworkHelper(student)?.id ?? -1;
}

function areHelperSelectionsEqual(left: Record<number, number>, right: Record<number, number>) {
	const leftKeys = Object.keys(left);
	const rightKeys = Object.keys(right);

	if (leftKeys.length !== rightKeys.length) {
		return false;
	}

	return leftKeys.every((key) => left[Number(key)] === right[Number(key)]);
}

